// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomerFormGroup } from '@interfaces/create-deal-inputs.interface';
import { debounceTime, takeWhile } from 'rxjs';
import { UserService } from '../../../../services/user.service';
import { emailPattern, namePattern, phoneImporter, showFormErrors, usPhonePattern } from '../../../../utils/form-utils';

@Component({
    selector: 'app-customer-form-component',
    templateUrl: './customer-form.component.html',
    styleUrls: ['./customer-form.component.scss'],
})
export class CustomerFormComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() customerFormProperties: CustomerFormGroup;
    @Input() disabledInputs: string[] = [];
    @Input() triggerFormErrors: boolean;
    @Input() isBuyer: boolean;
    @Output() attachForm = new EventEmitter<FormGroup>();
    @Output() goToEditView = new EventEmitter<FormGroup>();
    @Output() disableNext = new EventEmitter<boolean>();
    customerForm: FormGroup;
    private isComponentAlive = true;
    @Output() isLoading = new EventEmitter<boolean>();
    @Output() isUserFound = new EventEmitter<any>();
    @Output() logonId = new EventEmitter<string>();

    constructor(
        private fb: FormBuilder,
        private userService: UserService
    ) {
        this.buildForm();
    }

    ngOnInit(): void {
        this.isLoading.emit(null);
        if (this.customerFormProperties) {
            this.setFormValues();
            this.disableInputs();
        }

        this.formChanges();
    }

    ngAfterViewInit(): void {
        this.isLoading.emit(false);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.triggerFormErrors?.currentValue) {
            showFormErrors(this.customerForm.controls);
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    formChanges(): void {
        this.customerForm.valueChanges.pipe(takeWhile(() => this.isComponentAlive)).subscribe((value) => {
            this.attachForm.emit(value);
        });

        this.customerForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.customerForm.controls.email.setValue(value.trim());
            });
    }

    buildForm() {
        this.customerForm = this.fb.group({
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            phone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
        });
    }

    findUser() {
        if (!this.customerFormProperties?.email || !this.customerForm.controls['email'].valid) {
            return;
        }

        let foundUser;
        this.isLoading.emit(true);
        this.disableNext.emit(false);

        this.userService
            .checkUserByEmail$(this.customerFormProperties.email)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(
                (user) => {
                    foundUser = user;
                },
                (error) => {
                    console.error('customer form error:', error);
                    this.isLoading.emit(false);
                },
                () => {
                    if (foundUser && foundUser.userId) {
                        this.customerFormProperties.firstName = foundUser.firstName;
                        this.customerFormProperties.lastName = foundUser.lastName;
                        this.customerFormProperties.phone = foundUser.phone;
                        this.customerFormProperties.email = foundUser.email;
                        this.logonId.emit(foundUser.logonId);

                        this.enableInputs();
                        this.setFormValues();

                        this.customerForm.patchValue({
                            phone: `*** *** ${foundUser.phone.substring(foundUser.phone.length - 4)}`,
                        });

                        //Disable all but the email so that it can be changed if needed.
                        this.customerForm.get('firstName').disable({ emitEvent: true, onlySelf: true });
                        this.customerForm.get('lastName').disable({ emitEvent: true, onlySelf: true });
                        this.customerForm.get('phone').disable({ emitEvent: true, onlySelf: true });

                        this.disableNext.emit(false);
                        this.isUserFound.emit(foundUser);
                    } else {
                        this.enableInputs();
                        this.setFormValues();
                        this.isUserFound.emit(false);
                        this.logonId.emit(null);
                        this.disableNext.emit(true);
                    }
                    this.isLoading.emit(false);
                }
            );
    }

    setFormValues(): void {
        this.customerForm.setValue({
            firstName: this.customerFormProperties.firstName ? this.customerFormProperties.firstName : '',
            lastName: this.customerFormProperties.lastName ? this.customerFormProperties.lastName : '',
            email: this.customerFormProperties.email ? this.customerFormProperties.email : '',
            phone: this.customerFormProperties.phone
                ? this.customerFormProperties.phone.includes('*')
                    ? this.customerFormProperties.phone
                    : phoneImporter(this.customerFormProperties.phone)
                : '',
        });
    }

    //Need to enable fields before updating when finding a user since disbaled fields will be empty
    enableInputs(): void {
        this.customerForm.get('firstName').enable({ emitEvent: true, onlySelf: true });
        this.customerForm.get('lastName').enable({ emitEvent: true, onlySelf: true });
        this.customerForm.get('phone').enable({ emitEvent: true, onlySelf: true });
        this.customerForm.get('email').enable({ emitEvent: true, onlySelf: true });
    }

    disableInputs(): void {
        this.disabledInputs.forEach((input) => {
            this.customerForm.get(input).disable();
        });
    }
}
